import * as React from "react";
import { useRef, useMemo } from "react";
import { Media as MediaType } from "../types/Media";
import { Video } from "./Video";
import { Gallery } from "./Gallery";
import { useDimensions } from "../hooks/useDimensions";
import { useRemSize } from "../hooks/useRemSize";

interface MediaProps {
    media: MediaType[];
    cookieConsent: Record<string, boolean>;
}

export const Media: React.FC<MediaProps> = ({ media, cookieConsent }) => {
    const container = useRef<HTMLDivElement>();
    const [containerWidth] = useDimensions(container);
    const remSize = useRemSize();

    // Get the media we want to show on the front screen.
    const mediaToShow = useMemo(
        () => {
            // Get the first video (should only ever be one)
            const video = media.find(item => item.type === "video");

            // And the first image
            const coverImage = media.find(item => item.type === "image");

            // And return them as an array - removing anything falsey
            return [video, coverImage].filter(item => !!item);
        },
        [media]
    );

    // Work out the height to use for the row
    const height = useMemo(
        () => {
            // If there's no container width yet, short-circuit
            if (!containerWidth) return 0;

            // If there's only one item, we can just use its own height,
            // but no bigger than 360px.
            if (mediaToShow.length == 1) {
                return Math.min(mediaToShow[0].dimensions.height, 360);
            }

            // Otherwise we want to work it out with some
            // weird math that I dont really understand.
            const area = containerWidth - remSize * 2;
            const aspect = mediaToShow.reduce((acc, item) => {
                const { width, height } = item.dimensions;

                return acc + width / height;
            }, 0);

            return area / aspect;
        },
        [mediaToShow, containerWidth]
    );

    // Map our media into simple structures that we can use
    // to cleanly render our items.
    const items = useMemo(
        () => {
            return mediaToShow.map(({ key, type, url, dimensions }) => ({
                key,
                Component: type === "video" ? Video : Gallery,
                props: {
                    cookieConsent,
                    url,
                    height,
                    width: height * dimensions.aspect_ratio,
                    media
                }
            }));
        },
        [media, mediaToShow, height]
    );

    // Render everything out.
    return (
        <div className="media-container" ref={container}>
            {items.map(({ key, props, Component }) => (
                <Component key={key} {...props} />
            ))}
        </div>
    );
};

Media.displayName = "Media";
